import React, { useCallback, useEffect, useState } from 'react';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { EikaModal } from '../../components/EikaModal';
import { AvtaleGiroActionType } from '../../components/transaction/AvtaleGiro/form/actions';
import avtalegiroReducer from '../../components/transaction/AvtaleGiro/form/reducer';
import { getProposalState } from '../../components/transaction/AvtaleGiro/form/state';
import useFormContext, { AvtalegiroFormContext } from '../../components/transaction/AvtaleGiro/form/useFormContext';
import useValidate from '../../components/transaction/AvtaleGiro/form/validate';
import { avtalegiroSelector, useAccounts } from '../../fetchHooks/useAccount';
import type { AvtalegiroProposal } from '../../generated-types';
import { useReceiver } from '../../hooks/useSetReceiver';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { paymentTrackIDs } from '../../utils/gtm/trackIDs/payment';
import AvtaleGiroProposalsForm from './AvtaleGiroProposalsForm';

interface Props {
    avtaleGiroProposal: AvtalegiroProposal;
    show: boolean;
    setShow: React.Dispatch<boolean>;
    onClose: () => void;
    refreshAvtaleGiroProposal: () => void;
    refreshAvtalegiro: () => void;
}

const AvtaleGiroProposalsDetails: React.FC<Props> = ({
    avtaleGiroProposal,
    refreshAvtaleGiroProposal,
    show,
    setShow,
    onClose,
    refreshAvtalegiro,
}) => {
    const { signingStatus, signingError, signingOperationPending, dispatch } = useSigningContext();

    const accounts = useAccounts({ select: avtalegiroSelector });

    const receiver = useReceiver(avtaleGiroProposal.proposal.toAccountNumber);
    const { handleChange } = useFormContext();

    const [isDeleted, setDeleted] = useState(false);

    useEffect(() => {
        if (receiver) {
            handleChange({ type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED, payload: receiver });
        }
    }, [receiver, handleChange]);

    const updateOnChange = useCallback(() => {
        const hasChanged = !!signingError || !!signingStatus || signingOperationPending || isDeleted;
        if (hasChanged) {
            refreshAvtaleGiroProposal();
            refreshAvtalegiro();
        }
    }, [signingError, signingStatus, signingOperationPending, isDeleted, refreshAvtaleGiroProposal, refreshAvtalegiro]);

    const onCloseAvtaleGiro = useCallback(() => {
        updateOnChange();
        handleChange({ type: AvtaleGiroActionType.RESET });
        dispatch({ type: Actions.RESET });
        onClose();
    }, [updateOnChange, handleChange, dispatch, onClose]);

    return (
        <EikaModal
            id="avtaleGiroProposalsId"
            className="avtalegiro-details-proposal-modal"
            open={show}
            setOpen={setShow}
            contentShouldStretch
            timeOut={signingStatus === 'COMPLETED' || isDeleted ? 2500 : undefined}
            size="wide"
            onClose={onCloseAvtaleGiro}
            desktopMinHeight="small"
            appNamespace="dagligbank-lokalbank-web"
            dataTrackName={paymentTrackIDs.avtalegiro.proposalName}
        >
            <AvtaleGiroProposalsForm
                creditorName={avtaleGiroProposal.creditorName}
                accounts={accounts.data ?? []}
                accountState={{ isLoading: accounts.isLoading, refetch: accounts.refetch }}
                receiver={receiver}
                onClose={onCloseAvtaleGiro}
                proposalId={avtaleGiroProposal.id}
                onDeleted={() => setDeleted(true)}
            />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_AVTALEGIRO}>
        <AvtalegiroFormContext
            validateHook={useValidate}
            reducer={avtalegiroReducer}
            initialState={getProposalState(props.avtaleGiroProposal)}
        >
            <AvtaleGiroProposalsDetails {...props} />
        </AvtalegiroFormContext>
    </SigningContextProvider>
);
